import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Button, Form, Table, Badge, Modal,
  Spinner, Nav, Tab, InputGroup, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import {
  Plus, PencilSquare, Archive, Play, Pause,
  ArrowRepeat, CheckCircle, XCircle, Telephone,
  Clock, Person, GraphUp, Copy, PersonCheck,
} from 'react-bootstrap-icons';
import { Gift } from 'lucide-react';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { loyaltyAPI, branchAPI, randomRewardAPI } from '../../services';
import { useAuth } from '../../context/AuthProvider';
import { toast } from 'react-toastify';
import { formatDistanceToNow, format } from 'date-fns';
import { vi } from 'date-fns/locale';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function formatVND(n) {
  return Number(n || 0).toLocaleString('vi-VN');
}

function timeAgo(date) {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: vi });
  } catch {
    return '—';
  }
}

const STATUS_META = {
  ACTIVE:   { label: 'Hoạt động',  bg: 'success' },
  PAUSED:   { label: 'Tạm dừng',   bg: 'warning'  },
  ARCHIVED: { label: 'Lưu trữ',    bg: 'secondary' },
};

const PARTICIPATION_STATUS_META = {
  PENDING_REVIEW: { label: 'Chờ duyệt', bg: 'warning'   },
  APPROVED:       { label: 'Đã duyệt',  bg: 'success'   },
  REJECTED:       { label: 'Đã từ chối', bg: 'danger'   },
};

const EMPTY_FORM = {
  name: '',
  slug: '',
  discount_type: 'ITEM_PERCENTAGE',
  discount_percentage: 20,
  discount_max_amount: 15000,
  page_slug: '',
  redis_ttl_seconds: 900,
  // evidence_config — yêu cầu input
  ec_require_phone: true,
  ec_require_name: false,
  ec_require_image: true,
  evidence_min_images: 1,
  evidence_max_images: 5,
  evidence_description: 'Chụp màn hình xác nhận',
  // evidence_config — behavior
  ec_auto_approve: false,
  ec_max_uses: 1, // 0 = unlimited
  status: 'ACTIVE',
  branch_id: null, // null = toàn chuỗi (chỉ admin tạo được), string = 1 chi nhánh cụ thể
  use_random_reward: false,
  random_distribution_id: null,
  stackable: false, // true = cộng dồn được với CT khác trong cùng đơn
};

// ─────────────────────────────────────────────────────────────────────────────
// Stats Card
// ─────────────────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, color }) {
  return (
    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
      <Card.Body className="d-flex align-items-center gap-3 p-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{ width: 48, height: 48, background: color + '18' }}
        >
          <span style={{ color, fontSize: 22 }}>{icon}</span>
        </div>
        <div>
          <div className="text-muted small mb-0" style={{ fontSize: 12, fontWeight: 500 }}>
            {label}
          </div>
          <div className="fw-bold fs-5 lh-1 mt-1" style={{ color: '#1a1a2e' }}>
            {value ?? '—'}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Program Card
// ─────────────────────────────────────────────────────────────────────────────

function ProgramCard({ program, onEdit, onToggleStatus, onArchive, onCopy }) {
  const meta = STATUS_META[program.status] ?? STATUS_META.ARCHIVED;
  const isActive = program.status === 'ACTIVE';
  const isArchived = program.status === 'ARCHIVED';

  const discountLabel =
    program.discount_type === 'ITEM_PERCENTAGE'
      ? `Giảm ${program.discount_percentage}%`
      : `Giảm ${formatVND(program.discount_percentage)}đ`;

  return (
    <Card
      className="border-0 shadow-sm mb-3"
      style={{ borderRadius: 14, transition: 'box-shadow 0.18s', opacity: isArchived ? 0.65 : 1 }}
      onMouseEnter={(e) => !isArchived && (e.currentTarget.style.boxShadow = '0 6px 24px rgba(13,148,136,.13)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-start gap-3">
          {/* Icon circle */}
          <div
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 48, height: 48,
              background: 'linear-gradient(135deg, #0d9488 0%, #7c3aed 100%)',
            }}
          >
            <Gift size={22} color="#fff" />
          </div>

          {/* Content */}
          <div className="flex-grow-1 min-width-0">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <span className="fw-bold" style={{ fontSize: 15 }}>{program.name}</span>
              <Badge bg={meta.bg} pill style={{ fontSize: 11 }}>{meta.label}</Badge>
              <Badge
                style={{
                  background: 'linear-gradient(90deg,#0d9488,#0891b2)',
                  fontSize: 11, fontWeight: 600,
                }}
                pill
              >
                {discountLabel}
                {program.discount_max_amount > 0 && ` · Tối đa ${formatVND(program.discount_max_amount)}đ`}
              </Badge>
              {program.stackable && (
                <OverlayTrigger
                  overlay={<Tooltip>KH có thể tham gia thêm CT này dù đã có CT khác trong cùng đơn</Tooltip>}
                >
                  <Badge
                    bg="purple"
                    pill
                    style={{ fontSize: 11, fontWeight: 600, background: '#7c3aed' }}
                  >
                    ＋ Cộng dồn
                  </Badge>
                </OverlayTrigger>
              )}
            </div>
            <div className="text-muted small">
              <code style={{ fontSize: 11, background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>
                {program.slug}
              </code>
              {program.page_slug && (
                <span className="ms-2 text-muted" style={{ fontSize: 11 }}>
                  → trang: <em>{program.page_slug}</em>
                </span>
              )}
              <div className="mt-1">
                {program.branch ? (
                  <Badge bg="info" pill style={{ fontSize: 10 }}>
                    📍 {program.branch.name}
                  </Badge>
                ) : (
                  <Badge bg="secondary" pill style={{ fontSize: 10 }}>🌐 Toàn chuỗi</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="d-flex gap-1 flex-shrink-0">
            {!isArchived && (
              <>
                <OverlayTrigger overlay={<Tooltip>Chỉnh sửa</Tooltip>}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => onEdit(program)}
                    style={{ borderRadius: 8, padding: '4px 10px' }}
                  >
                    <PencilSquare size={14} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger overlay={<Tooltip>{isActive ? 'Tạm dừng' : 'Kích hoạt'}</Tooltip>}>
                  <Button
                    variant={isActive ? 'outline-warning' : 'outline-success'}
                    size="sm"
                    onClick={() => onToggleStatus(program)}
                    style={{ borderRadius: 8, padding: '4px 10px' }}
                  >
                    {isActive ? <Pause size={14} /> : <Play size={14} />}
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger overlay={<Tooltip>Lưu trữ</Tooltip>}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onArchive(program)}
                    style={{ borderRadius: 8, padding: '4px 10px' }}
                  >
                    <Archive size={14} />
                  </Button>
                </OverlayTrigger>
              </>
            )}
            <OverlayTrigger overlay={<Tooltip>Copy sang chi nhánh</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onCopy(program)}
                style={{ borderRadius: 8, padding: '4px 10px' }}
              >
                <Copy size={14} />
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Footer */}
        <div
          className="d-flex gap-3 mt-2 pt-2 text-muted"
          style={{ borderTop: '1px solid #f1f5f9', fontSize: 12 }}
        >
          <span>
            <Person size={12} className="me-1" />
            {program._count?.participations ?? 0} lượt tham gia
          </span>
          <span>
            <Clock size={12} className="me-1" />
            {timeAgo(program.updated_at)}
          </span>
          <span>
            <Clock size={12} className="me-1" />
            Chặn trùng {Math.round(program.redis_ttl_seconds / 60)} phút
          </span>
        </div>
      </Card.Body>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Program Form Modal
// ─────────────────────────────────────────────────────────────────────────────

function ProgramFormModal({ show, onHide, program, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [distributions, setDistributions] = useState([]);

  useEffect(() => {
    if (!show) return;
    randomRewardAPI.list({ is_active: true, limit: 100 })
      .then((res) => setDistributions(res?.data ?? res ?? []))
      .catch((err) => toast.error(err?.message || 'Không tải được danh sách phân phối'));
    // Chỉ admin cần load danh sách branch để chọn; manager tự động gắn branch của mình
    if (isAdmin) {
      branchAPI.getBranches({ is_active: true, limit: 100 })
        .then((res) => setBranches(res?.data ?? res ?? []))
        .catch((err) => toast.error(err?.message || 'Không tải được danh sách chi nhánh'));
    }

    if (program) {
      const ec = program.evidence_config || {};
      const isLegacyMembership = ec.type === 'membership';
      // Số lần tối đa: ưu tiên flag mới `max_uses`, fallback legacy column `max_uses_per_phone`
      const maxUses = ec.max_uses ?? program.max_uses_per_phone ?? 1;
      setForm({
        name: program.name || '',
        slug: program.slug || '',
        discount_type: program.discount_type || 'ITEM_PERCENTAGE',
        discount_percentage: Number(program.discount_percentage || 20),
        discount_max_amount: Number(program.discount_max_amount || 15000),
        page_slug: program.page_slug || '',
        redis_ttl_seconds: program.redis_ttl_seconds || 900,
        // evidence_config — yêu cầu input
        ec_require_phone: ec.require_phone ?? true,
        ec_require_name: ec.require_name ?? ec.collect_name ?? isLegacyMembership,
        ec_require_image: ec.require_image ?? !isLegacyMembership,
        evidence_min_images: ec.min_images || 1,
        evidence_max_images: ec.max_images || 5,
        evidence_description: ec.description || '',
        // evidence_config — behavior
        ec_auto_approve: ec.auto_approve ?? isLegacyMembership,
        ec_max_uses: maxUses,
        status: program.status || 'ACTIVE',
        branch_id: program.branch_id ?? null,
        use_random_reward: !!program.use_random_reward,
        random_distribution_id: program.random_distribution_id ?? null,
        stackable: !!program.stackable,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [show, program]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleNameChange = (name) => {
    setForm((f) => ({ ...f, name, slug: program ? f.slug : toSlug(name) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Vui lòng nhập tên chương trình'); return; }
    if (!form.slug.trim()) { toast.error('Vui lòng nhập slug'); return; }

    const maxUses = Math.max(0, Number(form.ec_max_uses) || 0);
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      discount_type: form.discount_type,
      discount_percentage: Number(form.discount_percentage),
      discount_max_amount: Number(form.discount_max_amount),
      page_slug: form.page_slug.trim() || null,
      redis_ttl_seconds: Number(form.redis_ttl_seconds),
      // Mirror max_uses vào legacy column để 2 nguồn không drift
      // (BE đọc từ evidence_config.max_uses qua resolveEvidenceConfig)
      max_uses_per_phone: maxUses === 0 ? 999999 : maxUses,
      evidence_config: {
        require_phone: !!form.ec_require_phone,
        require_name: !!form.ec_require_name,
        require_image: !!form.ec_require_image,
        min_images: Number(form.evidence_min_images),
        max_images: Number(form.evidence_max_images),
        description: form.evidence_description.trim(),
        auto_approve: !!form.ec_auto_approve,
        max_uses: maxUses,
      },
      status: form.status,
      branch_id: form.branch_id,
      use_random_reward: !!form.use_random_reward,
      random_distribution_id: form.use_random_reward ? form.random_distribution_id : null,
      stackable: !!form.stackable,
    };

    setSaving(true);
    try {
      if (program) {
        await loyaltyAPI.updateProgram(program.id, payload);
        toast.success('Đã cập nhật chương trình');
      } else {
        await loyaltyAPI.createProgram(payload);
        toast.success('Đã tạo chương trình mới');
      }
      onSaved();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Lỗi lưu chương trình');
    } finally {
      setSaving(false);
    }
  };

  const isPercentage = form.discount_type === 'ITEM_PERCENTAGE';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold" style={{ fontSize: 18 }}>
          <Gift size={20} className="me-2" style={{ color: '#0d9488' }} />
          {program ? 'Chỉnh sửa chương trình' : 'Thêm chương trình ưu đãi'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-3">
          {/* Row 1: Name + Slug */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold small">Tên chương trình <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="VD: Đánh giá Google Maps"
                required
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold small">Slug (URL)</Form.Label>
              <InputGroup>
                <Form.Control
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value)}
                  placeholder="google-maps-review"
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => set('slug', toSlug(form.name))}
                  title="Tạo lại từ tên"
                  type="button"
                >
                  <ArrowRepeat size={14} />
                </Button>
              </InputGroup>
            </Col>
          </Row>

          {/* Row 2: Discount type + Page slug */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold small">Loại giảm giá</Form.Label>
              <Form.Select value={form.discount_type} onChange={(e) => set('discount_type', e.target.value)}>
                <option value="ITEM_PERCENTAGE">Phần trăm theo món (ITEM_PERCENTAGE)</option>
                <option value="ITEM_AMOUNT">Số tiền cố định theo món (ITEM_AMOUNT)</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold small">Slug trang hướng dẫn</Form.Label>
              <Form.Control
                value={form.page_slug}
                onChange={(e) => set('page_slug', e.target.value)}
                placeholder="huong-dan-google-maps-review"
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <Form.Text className="text-muted">Liên kết đến trang CMS giải thích ưu đãi</Form.Text>
            </Col>
          </Row>

          {/* Row 3: Percentage + Max amount */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold small">
                {isPercentage ? 'Phần trăm giảm (%)' : 'Số tiền giảm (đ)'}
              </Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={isPercentage ? 100 : undefined}
                value={form.discount_percentage}
                onChange={(e) => set('discount_percentage', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold small">Giảm tối đa (VND)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.discount_max_amount}
                  onChange={(e) => set('discount_max_amount', e.target.value)}
                />
                <InputGroup.Text>đ</InputGroup.Text>
              </InputGroup>
            </Col>
          </Row>

          {/* Row 4: Anti-fraud TTL */}
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Label className="fw-semibold small">Thời gian chặn trùng lặp giữa các thiết bị (giây)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min={60}
                  value={form.redis_ttl_seconds}
                  onChange={(e) => set('redis_ttl_seconds', e.target.value)}
                />
                <InputGroup.Text>{Math.round(form.redis_ttl_seconds / 60)} phút</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Cùng SĐT từ thiết bị khác trong khoảng thời gian này → bị chặn (chống share SĐT/mã giảm giá).
              </Form.Text>
            </Col>
          </Row>

          {/* Evidence config — yêu cầu nhập từ khách */}
          <div
            className="rounded-3 p-3 mb-3"
            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
          >
            <div className="fw-semibold small mb-3" style={{ color: '#166534' }}>
              Yêu cầu nhập từ khách
            </div>

            {/* Field toggles — yêu cầu input từ khách */}
            <Form.Check
              type="switch"
              id="ec-require-phone"
              label="Yêu cầu số điện thoại (SĐT)"
              checked={!!form.ec_require_phone}
              onChange={(e) => set('ec_require_phone', e.target.checked)}
              className="mb-2"
            />
            <Form.Check
              type="switch"
              id="ec-require-name"
              label="Yêu cầu họ tên"
              checked={!!form.ec_require_name}
              onChange={(e) => set('ec_require_name', e.target.checked)}
              className="mb-2"
            />
            <Form.Check
              type="switch"
              id="ec-require-image"
              label="Yêu cầu ảnh xác nhận"
              checked={!!form.ec_require_image}
              onChange={(e) => set('ec_require_image', e.target.checked)}
              className="mb-3"
            />

            {/* Image count — only when require_image is on */}
            {!!form.ec_require_image && (
              <Row className="g-3 mb-3">
                <Col xs={6} md={4}>
                  <Form.Label className="small">Ảnh tối thiểu</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={10}
                    value={form.evidence_min_images}
                    onChange={(e) => set('evidence_min_images', e.target.value)}
                  />
                </Col>
                <Col xs={6} md={4}>
                  <Form.Label className="small">Ảnh tối đa</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={10}
                    value={form.evidence_max_images}
                    onChange={(e) => set('evidence_max_images', e.target.value)}
                  />
                </Col>
              </Row>
            )}

            {/* Description */}
            <Row className="g-3 mb-3">
              <Col>
                <Form.Label className="small">Hướng dẫn cho khách</Form.Label>
                <Form.Control
                  value={form.evidence_description}
                  onChange={(e) => set('evidence_description', e.target.value)}
                  placeholder="VD: Chụp màn hình đánh giá 5 sao trên Google Maps"
                />
              </Col>
            </Row>

            {/* Behavior — auto-approve + max_uses */}
            <hr className="my-3" style={{ borderColor: '#bbf7d0' }} />
            <div className="fw-semibold small mb-2" style={{ color: '#166534' }}>
              Cách xử lý &amp; giới hạn tham gia
            </div>

            <Form.Check
              type="switch"
              id="ec-auto-approve"
              label={
                <span>
                  Tự động duyệt (không cần NV xác nhận)
                  <Form.Text className="d-block text-muted" style={{ fontSize: 11 }}>
                    Bật cho chương trình kiểu &quot;đăng ký thành viên&quot;. Tắt để NV duyệt thủ công sau khi xem ảnh.
                  </Form.Text>
                </span>
              }
              checked={!!form.ec_auto_approve}
              onChange={(e) => set('ec_auto_approve', e.target.checked)}
              className="mb-3"
            />

            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Label className="small">Số lần tham gia tối đa</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    type="number"
                    min={0}
                    max={999}
                    value={form.ec_max_uses}
                    onChange={(e) => set('ec_max_uses', e.target.value)}
                  />
                  <InputGroup.Text>
                    {Number(form.ec_max_uses) === 0 ? '∞ unlimited' : `lần / SĐT & / thiết bị`}
                  </InputGroup.Text>
                </InputGroup>
                <Form.Text className="text-muted">
                  Áp dụng cho cả SĐT và thiết bị (lấy giá trị nào đạt trước → chặn). Đặt <strong>0</strong> = không giới hạn.
                </Form.Text>
              </Col>
            </Row>

            <hr className="my-3" style={{ borderColor: '#bbf7d0' }} />
            <Form.Check
              type="switch"
              id="ec-stackable"
              label={
                <span>
                  Cho phép cộng dồn với chương trình khác
                  <Form.Text className="d-block text-muted" style={{ fontSize: 11 }}>
                    Bật: KH có thể tham gia thêm CT này dù đã có CT khác trong cùng đơn (vd: CT thành viên + CT đánh giá Google).
                    Tắt (mặc định): mỗi đơn chỉ được áp 1 CT loại này; phải huỷ CT hiện tại trước khi đăng ký CT khác.
                  </Form.Text>
                </span>
              }
              checked={!!form.stackable}
              onChange={(e) => set('stackable', e.target.checked)}
              className="mb-2"
            />
          </div>

          {/* Branch selection */}
          <div
            className="rounded-3 p-3 mb-3"
            style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
          >
            <div className="fw-semibold small mb-2" style={{ color: '#1e40af' }}>
              Áp dụng chi nhánh
            </div>
            {isAdmin ? (
              <>
                <Form.Select
                  value={form.branch_id ?? ''}
                  onChange={(e) => set('branch_id', e.target.value || null)}
                  size="sm"
                >
                  <option value="">🌐 Toàn chuỗi (không giới hạn chi nhánh)</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Chọn chi nhánh cụ thể hoặc để trống = áp dụng toàn chuỗi.
                </Form.Text>
              </>
            ) : (
              <div className="text-muted small">
                📍 Chương trình sẽ được gắn vào chi nhánh của bạn.
              </div>
            )}
          </div>

          {/* Random Reward */}
          <div
            className="rounded-3 p-3 mb-3"
            style={{ background: '#faf5ff', border: '1px solid #e9d5ff' }}
          >
            <div className="fw-semibold small mb-2" style={{ color: '#6b21a8' }}>
              🎲 Random Reward (ngẫu nhiên có trọng số)
            </div>
            <Form.Check
              type="switch"
              id="loyalty-random-reward-switch"
              label="Bật random discount (ghi đè % cố định)"
              checked={!!form.use_random_reward}
              onChange={(e) => set('use_random_reward', e.target.checked)}
              className="mb-2"
            />
            {form.use_random_reward && (
              <Form.Select
                size="sm"
                value={form.random_distribution_id ?? ''}
                onChange={(e) => set('random_distribution_id', e.target.value || null)}
              >
                <option value="">— Chọn distribution —</option>
                {distributions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.context_key})
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              Khi bật, hệ thống sẽ roll % giảm theo bảng phân phối thay vì dùng giá trị cố định.
            </Form.Text>
          </div>

          {/* Status */}
          <Form.Check
            type="switch"
            id="loyalty-status-switch"
            label={
              <span>
                Trạng thái:{' '}
                <Badge bg={form.status === 'ACTIVE' ? 'success' : 'warning'} pill>
                  {form.status === 'ACTIVE' ? 'Hoạt động' : 'Tạm dừng'}
                </Badge>
              </span>
            }
            checked={form.status === 'ACTIVE'}
            onChange={(e) => set('status', e.target.checked ? 'ACTIVE' : 'PAUSED')}
          />
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Hủy
          </Button>
          <Button
            type="submit"
            disabled={saving}
            style={{ background: '#0d9488', border: 'none', minWidth: 120 }}
          >
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            {program ? 'Cập nhật' : 'Tạo chương trình'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reject Reason Modal
// ─────────────────────────────────────────────────────────────────────────────

function RejectModal({ show, onHide, onConfirm, processing }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (show) setReason(''); }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold" style={{ fontSize: 16 }}>Từ chối yêu cầu</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label className="small fw-semibold">Lý do từ chối</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Nhập lý do..."
        />
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={processing}>Hủy</Button>
        <Button
          variant="danger"
          size="sm"
          disabled={!reason.trim() || processing}
          onClick={() => onConfirm(reason)}
        >
          {processing ? <Spinner size="sm" className="me-1" /> : null}
          Xác nhận từ chối
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Copy Program Modal
// ─────────────────────────────────────────────────────────────────────────────

function CopyProgramModal({ show, onHide, program, onCopied }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [branches, setBranches] = useState([]);
  const [targetBranchId, setTargetBranchId] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!show) return;
    setTargetBranchId(isAdmin ? '' : (user?.branch_id ?? null));
    if (isAdmin) {
      branchAPI.getBranches({ is_active: true, limit: 100 })
        .then((res) => setBranches(res?.data ?? res ?? []))
        .catch((err) => toast.error(err?.message || 'Không tải được danh sách chi nhánh'));
    }
  }, [show]);

  const handleCopy = async () => {
    if (isAdmin && !targetBranchId && targetBranchId !== null) {
      toast.error('Vui lòng chọn chi nhánh đích');
      return;
    }
    setCopying(true);
    try {
      const branchId = isAdmin ? (targetBranchId || null) : null;
      await loyaltyAPI.copyProgram(program.id, branchId);
      toast.success('Đã copy chương trình thành công (trạng thái: Tạm dừng)');
      onCopied();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Lỗi khi copy chương trình');
    } finally {
      setCopying(false);
    }
  };

  if (!program) return null;

  const targetBranchName = branches.find((b) => b.id === targetBranchId)?.name;

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold" style={{ fontSize: 16 }}>
          <Copy size={16} className="me-2" style={{ color: '#0d9488' }} />
          Copy chương trình
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 p-2 rounded-2" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div className="fw-semibold small text-muted mb-1">Chương trình nguồn</div>
          <div className="fw-bold" style={{ fontSize: 14 }}>{program.name}</div>
          <div className="mt-1">
            {program.branch ? (
              <Badge bg="info" pill style={{ fontSize: 10 }}>📍 {program.branch.name}</Badge>
            ) : (
              <Badge bg="secondary" pill style={{ fontSize: 10 }}>🌐 Toàn chuỗi</Badge>
            )}
          </div>
        </div>

        {isAdmin ? (
          <Form.Group>
            <Form.Label className="fw-semibold small">Chi nhánh đích</Form.Label>
            <Form.Select
              value={targetBranchId ?? ''}
              onChange={(e) => setTargetBranchId(e.target.value || null)}
            >
              <option value="">🌐 Toàn chuỗi (không gắn chi nhánh)</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        ) : (
          <div className="text-muted small p-2 rounded-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            Chương trình sẽ được copy vào chi nhánh của bạn và đặt ở trạng thái <strong>Tạm dừng</strong>.
          </div>
        )}

        {isAdmin && targetBranchId && (
          <div className="mt-2 text-muted small">
            → Copy vào: <strong>{targetBranchName}</strong>
          </div>
        )}
        {isAdmin && !targetBranchId && (
          <div className="mt-2 text-muted small">→ Copy thành chương trình <strong>toàn chuỗi</strong></div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={copying}>Hủy</Button>
        <Button
          size="sm"
          disabled={copying}
          onClick={handleCopy}
          style={{ background: '#0d9488', border: 'none', minWidth: 100 }}
        >
          {copying ? <Spinner size="sm" className="me-1" /> : <Copy size={13} className="me-1" />}
          Copy ngay
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Programs Tab
// ─────────────────────────────────────────────────────────────────────────────

function ProgramsTab({ programs, loading, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);

  const handleEdit = (program) => { setEditing(program); setShowModal(true); };
  const handleAdd  = () => { setEditing(null); setShowModal(true); };
  const handleCopy = (program) => setCopyTarget(program);

  const handleToggleStatus = async (program) => {
    const next = program.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setProcessing(program.id);
    try {
      await loyaltyAPI.updateProgramStatus(program.id, next);
      toast.success(`Đã ${next === 'ACTIVE' ? 'kích hoạt' : 'tạm dừng'} chương trình`);
      onRefresh();
    } catch (err) {
      toast.error(err?.message || 'Lỗi cập nhật trạng thái');
    } finally {
      setProcessing(null);
    }
  };

  const handleArchive = async (program) => {
    if (!window.confirm(`Lưu trữ chương trình "${program.name}"? Thao tác này không thể hoàn tác.`)) return;
    setProcessing(program.id);
    try {
      await loyaltyAPI.updateProgramStatus(program.id, 'ARCHIVED');
      toast.success('Đã lưu trữ chương trình');
      onRefresh();
    } catch (err) {
      toast.error(err?.message || 'Lỗi lưu trữ');
    } finally {
      setProcessing(null);
    }
  };

  const totalParticipations = programs.reduce((s, p) => s + (p._count?.participations ?? 0), 0);
  const activeCount  = programs.filter((p) => p.status === 'ACTIVE').length;

  return (
    <>
      {/* Stats */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={4}>
          <StatCard icon={<Gift size={20} />} label="Tổng chương trình" value={programs.length} color="#0d9488" />
        </Col>
        <Col xs={12} sm={4}>
          <StatCard icon={<CheckCircle size={20} />} label="Đang hoạt động" value={activeCount} color="#16a34a" />
        </Col>
        <Col xs={12} sm={4}>
          <StatCard icon={<GraphUp size={20} />} label="Tổng lượt tham gia" value={totalParticipations} color="#7c3aed" />
        </Col>
      </Row>

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="fw-bold mb-0">Danh sách chương trình</h5>
          <small className="text-muted">Quản lý các chương trình ưu đãi khách hàng</small>
        </div>
        <Button
          onClick={handleAdd}
          style={{ background: '#0d9488', border: 'none', borderRadius: 10 }}
        >
          <Plus size={18} className="me-1" />
          Thêm chương trình
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner style={{ color: '#0d9488' }} />
        </div>
      ) : programs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <Gift size={40} color="#cbd5e1" />
          <p className="mt-3 mb-2 fw-semibold">Chưa có chương trình ưu đãi nào</p>
          <Button variant="outline-secondary" size="sm" onClick={handleAdd}>
            <Plus size={14} className="me-1" /> Tạo ngay
          </Button>
        </div>
      ) : (
        programs.map((p) => (
          <ProgramCard
            key={p.id}
            program={p}
            disabled={processing === p.id}
            onEdit={handleEdit}
            onToggleStatus={handleToggleStatus}
            onArchive={handleArchive}
            onCopy={handleCopy}
          />
        ))
      )}

      <ProgramFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        program={editing}
        onSaved={onRefresh}
      />

      <CopyProgramModal
        show={!!copyTarget}
        onHide={() => setCopyTarget(null)}
        program={copyTarget}
        onCopied={onRefresh}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Participations Tab
// ─────────────────────────────────────────────────────────────────────────────

function getDiscountLabel(item) {
  const snap = item.rolled_reward_snapshot;
  if (snap) {
    if (snap.type === 'PERCENTAGE') return `${snap.value}%`;
    if (snap.type === 'FIXED_AMOUNT') return `${formatVND(snap.value)}đ`;
    return '—';
  }
  const prog = item.program;
  if (!prog) return '—';
  if (prog.discount_type === 'ITEM_PERCENTAGE') return `${Number(prog.discount_percentage)}%`;
  return `${formatVND(prog.discount_max_amount)}đ`;
}

function ParticipationsTab({ programs = [] }) {
  const { selectedBranch } = useBranch();
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [statusFilter, setStatus]   = useState('');
  const [programFilter, setProgram] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [phoneSearch, setPhone]     = useState('');
  const [includeUnlinked, setIncludeUnlinked] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [previewImg, setPreviewImg]  = useState(null);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({ total: 0 });
  const LIMIT = 20;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, page };
      if (statusFilter) params.status = statusFilter;
      if (programFilter) params.programId = programFilter;
      if (selectedBranch?.id) params.branchId = selectedBranch.id;
      if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        params.dateTo = end.toISOString();
      }
      // Mã treo chưa liên kết đơn (orphan) bị ẩn mặc định với PENDING_REVIEW —
      // bật để audit/dọn thủ công.
      if (includeUnlinked) params.includeUnlinked = true;
      const res = await loyaltyAPI.getParticipations(params);
      setItems(Array.isArray(res) ? res : (res?.data ?? []));
      setMeta(res?.meta ?? { total: 0 });
    } catch (err) {
      toast.error(err?.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, programFilter, selectedBranch, dateFrom, dateTo, page, includeUnlinked]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // client-side phone filter
  const displayed = phoneSearch.trim()
    ? items.filter((i) => i.customer_phone?.includes(phoneSearch.trim()))
    : items;

  const handleApprove = async (id) => {
    const orderItemId = window.prompt('Nhập Order Item ID để áp dụng giảm giá:');
    if (!orderItemId?.trim()) return;
    setProcessing(id);
    try {
      const res = await loyaltyAPI.approveParticipation(id, orderItemId.trim());
      toast.success(`Đã duyệt — Giảm ${formatVND(res.discountAmount)}đ cho "${res.menuItemName}"`);
      fetchItems();
    } catch (err) {
      toast.error(err?.message || 'Lỗi duyệt yêu cầu');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectConfirm = async (reason) => {
    setProcessing(rejectTarget);
    try {
      await loyaltyAPI.rejectParticipation(rejectTarget, reason);
      toast.success('Đã từ chối yêu cầu');
      setRejectTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.message || 'Lỗi từ chối');
    } finally {
      setProcessing(null);
    }
  };

  // Dọn (reject hàng loạt) các mã treo orphan đang PENDING (chưa liên kết đơn).
  const orphanPending = displayed.filter(
    (i) => i.status === 'PENDING_REVIEW' && !i.order_item_id,
  );
  const handleBulkRejectOrphans = async () => {
    if (orphanPending.length === 0) return;
    if (!window.confirm(`Từ chối ${orphanPending.length} mã treo chưa liên kết đơn?`)) {
      return;
    }
    setBulkProcessing(true);
    let ok = 0;
    for (const item of orphanPending) {
      try {
        await loyaltyAPI.rejectParticipation(
          item.id,
          'Dọn thủ công: mã treo chưa liên kết đơn',
        );
        ok += 1;
      } catch {
        // bỏ qua từng lỗi, tiếp tục dọn các mã còn lại
      }
    }
    setBulkProcessing(false);
    toast.success(`Đã dọn ${ok}/${orphanPending.length} mã treo`);
    fetchItems();
  };

  return (
    <>
      {/* Filters */}
      <Row className="g-2 mb-3 align-items-end">
        <Col xs={12} sm={6} md={3}>
          <Form.Label className="small fw-semibold mb-1">Trạng thái</Form.Label>
          <Form.Select size="sm" value={statusFilter} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">Tất cả</option>
            <option value="PENDING_REVIEW">Chờ duyệt</option>
            <option value="APPROVED">Đã duyệt</option>
            <option value="REJECTED">Đã từ chối</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Label className="small fw-semibold mb-1">Chương trình</Form.Label>
          <Form.Select size="sm" value={programFilter} onChange={(e) => { setProgram(e.target.value); setPage(1); }}>
            <option value="">Tất cả chương trình</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label className="small fw-semibold mb-1">Từ ngày</Form.Label>
          <Form.Control
            size="sm"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          />
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label className="small fw-semibold mb-1">Đến ngày</Form.Label>
          <Form.Control
            size="sm"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          />
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Form.Label className="small fw-semibold mb-1">Tìm SĐT</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Text><Telephone size={13} /></InputGroup.Text>
            <Form.Control
              value={phoneSearch}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0901..."
            />
          </InputGroup>
        </Col>
        <Col xs="auto">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={fetchItems}
            disabled={loading}
            style={{ borderRadius: 8 }}
          >
            <ArrowRepeat size={14} className={loading ? 'spin' : ''} />
          </Button>
        </Col>
        <Col xs={12} className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="loyalty-include-unlinked"
            label="Hiện mã treo chưa liên kết đơn (orphan)"
            checked={includeUnlinked}
            onChange={(e) => { setIncludeUnlinked(e.target.checked); setPage(1); }}
            className="small"
          />
          {includeUnlinked && orphanPending.length > 0 && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleBulkRejectOrphans}
              disabled={bulkProcessing}
              style={{ borderRadius: 8 }}
            >
              {bulkProcessing
                ? <Spinner size="sm" />
                : <><XCircle size={13} className="me-1" />Dọn {orphanPending.length} mã treo</>}
            </Button>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table hover className="align-middle small" style={{ minWidth: 800 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={{ width: 40, fontWeight: 600 }}>#</th>
              <th style={{ fontWeight: 600 }}>Chương trình</th>
              <th style={{ fontWeight: 600 }}>Khuyến mãi</th>
              <th style={{ fontWeight: 600 }}>SĐT khách</th>
              <th style={{ fontWeight: 600 }}>Chi nhánh</th>
              <th style={{ fontWeight: 600 }}>Thời gian</th>
              <th style={{ fontWeight: 600 }}>Trạng thái</th>
              <th style={{ fontWeight: 600 }}>Ảnh XN</th>
              <th style={{ fontWeight: 600 }}>Người duyệt</th>
              <th style={{ fontWeight: 600 }}>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="text-center py-5">
                  <Spinner size="sm" style={{ color: '#0d9488' }} />
                </td>
              </tr>
            ) : displayed.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-5 text-muted">
                  Không có dữ liệu
                </td>
              </tr>
            ) : (
              displayed.map((item, idx) => {
                const stMeta = PARTICIPATION_STATUS_META[item.status] ?? PARTICIPATION_STATUS_META.PENDING_REVIEW;
                const isPending = item.status === 'PENDING_REVIEW';

                return (
                  <tr key={item.id} style={{ opacity: processing === item.id ? 0.5 : 1 }}>
                    <td className="text-muted">{(page - 1) * LIMIT + idx + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 28, height: 28, background: 'linear-gradient(135deg,#0d9488,#7c3aed)' }}
                        >
                          <Gift size={13} color="#fff" />
                        </div>
                        <span className="fw-semibold">{item.program?.name ?? '—'}</span>
                      </div>
                    </td>
                    <td>
                      <Badge
                        pill
                        style={{
                          background: 'linear-gradient(90deg,#0d9488,#0891b2)',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {getDiscountLabel(item)}
                      </Badge>
                    </td>
                    <td>
                      {item.customer_phone
                        ? <><Telephone size={12} className="me-1 text-muted" />{item.customer_phone}</>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>{item.branch?.name ?? item.branch_id}</td>
                    <td className="text-muted">{timeAgo(item.created_at)}</td>
                    <td>
                      <Badge bg={stMeta.bg} pill style={{ fontSize: 11 }}>{stMeta.label}</Badge>
                      {item.status === 'APPROVED' && item.applied_discount_amount > 0 && (
                        <div className="text-success" style={{ fontSize: 11, fontWeight: 600 }}>
                          −{formatVND(item.applied_discount_amount)}đ
                          {item.order_item?.menu_item?.name && ` · ${item.order_item.menu_item.name}`}
                        </div>
                      )}
                      {item.status === 'REJECTED' && item.rejected_reason && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {item.rejected_reason}
                        </div>
                      )}
                    </td>
                    <td>
                      {item.evidence_urls?.length > 0 ? (
                        <div className="d-flex gap-1">
                          {item.evidence_urls.slice(0, 3).map((url, i) => (
                            <button
                              key={i}
                              type="button"
                              className="border-0 p-0 bg-transparent"
                              onClick={() => setPreviewImg(url)}
                            >
                              <img
                                src={url}
                                alt=""
                                style={{
                                  width: 36, height: 36,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid #e2e8f0',
                                  cursor: 'pointer',
                                }}
                              />
                            </button>
                          ))}
                          {item.evidence_urls.length > 3 && (
                            <span className="text-muted small align-self-center">+{item.evidence_urls.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    {/* Người duyệt */}
                    <td>
                      {item.reviewer ? (
                        <div className="d-flex align-items-center gap-2">
                          {item.reviewer.avatar_url ? (
                            <img
                              src={item.reviewer.avatar_url}
                              alt={item.reviewer.full_name}
                              style={{
                                width: 26, height: 26,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1px solid #e2e8f0',
                              }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: 26, height: 26, background: '#f1f5f9' }}
                            >
                              <PersonCheck size={13} style={{ color: '#64748b' }} />
                            </div>
                          )}
                          <div style={{ lineHeight: 1.3 }}>
                            <div className="fw-semibold" style={{ fontSize: 12 }}>
                              {item.reviewer.full_name}
                            </div>
                            {item.reviewed_at && (
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                {format(new Date(item.reviewed_at), 'HH:mm dd/MM')}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Thao tác */}
                    <td>
                      {isPending && (
                        <div className="d-flex gap-1">
                          <Button
                            variant="success"
                            size="sm"
                            style={{ borderRadius: 8, fontSize: 12 }}
                            onClick={() => handleApprove(item.id)}
                            disabled={processing !== null}
                          >
                            {processing === item.id ? <Spinner size="sm" /> : <><CheckCircle size={12} className="me-1" />Duyệt</>}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ borderRadius: 8, fontSize: 12 }}
                            onClick={() => setRejectTarget(item.id)}
                            disabled={processing !== null}
                          >
                            <XCircle size={12} className="me-1" />Từ chối
                          </Button>
                        </div>
                      )}
                      {item.status === 'APPROVED' && (
                        <span className="text-success small d-flex align-items-center gap-1">
                          <CheckCircle size={13} /> Đã duyệt
                        </span>
                      )}
                      {item.status === 'REJECTED' && (
                        <span className="text-danger small d-flex align-items-center gap-1">
                          <XCircle size={13} /> Đã từ chối
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {meta.total > LIMIT && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small className="text-muted">Tổng {meta.total} kết quả</small>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              ‹ Trước
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={page * LIMIT >= meta.total} onClick={() => setPage(p => p + 1)}>
              Sau ›
            </Button>
          </div>
        </div>
      )}

      {/* Reject modal */}
      <RejectModal
        show={!!rejectTarget}
        onHide={() => setRejectTarget(null)}
        onConfirm={handleRejectConfirm}
        processing={!!processing}
      />

      {/* Image preview modal */}
      <Modal show={!!previewImg} onHide={() => setPreviewImg(null)} centered size="lg">
        <Modal.Header closeButton className="border-0" />
        <Modal.Body className="p-2 text-center">
          {previewImg && (
            <img src={previewImg} alt="Ảnh xác nhận" style={{ maxWidth: '100%', borderRadius: 10 }} />
          )}
        </Modal.Body>
      </Modal>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function LoyaltyManagementPage() {
  const [programs, setPrograms]   = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [activeTab, setActiveTab] = useState('programs');

  const fetchPrograms = useCallback(async () => {
    setLoadingPrograms(true);
    try {
      const res = await loyaltyAPI.getPrograms();
      setPrograms(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      toast.error(err?.message || 'Không thể tải chương trình ưu đãi');
    } finally {
      setLoadingPrograms(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  return (
    <PageLayout>
      {/* Page header */}
      <div
        className="rounded-4 p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 60%, #7c3aed 100%)',
          color: '#fff',
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.2)' }}
          >
            <Gift size={28} color="#fff" />
          </div>
          <div>
            <h4 className="fw-bold mb-0">Quản lý ưu đãi khách hàng</h4>
            <small style={{ opacity: 0.85 }}>
              Tạo và quản lý các chương trình giảm giá — duyệt yêu cầu tham gia từ khách hàng
            </small>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav
          variant="pills"
          className="mb-4"
          style={{ gap: 6 }}
        >
          <Nav.Item>
            <Nav.Link
              eventKey="programs"
              style={{
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 14,
                padding: '8px 20px',
                background: activeTab === 'programs' ? '#0d9488' : 'transparent',
                color: activeTab === 'programs' ? '#fff' : '#64748b',
                border: activeTab === 'programs' ? 'none' : '1px solid #e2e8f0',
              }}
            >
              <Gift size={14} className="me-2" />
              Chương trình ưu đãi
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="participations"
              style={{
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 14,
                padding: '8px 20px',
                background: activeTab === 'participations' ? '#0d9488' : 'transparent',
                color: activeTab === 'participations' ? '#fff' : '#64748b',
                border: activeTab === 'participations' ? 'none' : '1px solid #e2e8f0',
              }}
            >
              <Person size={14} className="me-2" />
              Lịch sử tham gia
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="programs">
            <ProgramsTab
              programs={programs}
              loading={loadingPrograms}
              onRefresh={fetchPrograms}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="participations">
            <ParticipationsTab programs={programs} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </PageLayout>
  );
}
